import { memo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Product, ProductCategory } from "@/types/product";
import { Edit, Trash2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/hooks/useAuth";

interface ProductCardProps {
  product: Product;
  category?: ProductCategory;
  onEdit: (product: Product) => void;
  onDelete: (id: string) => void;
  onClick: (product: Product) => void;
  getCategoryName: (categoryId: string) => string;
  getCategoryColor: (categoryId: string) => string;
}

function ProductCardContent({
  product,
  category,
  onEdit,
  onDelete,
  onClick,
  getCategoryName,
  getCategoryColor,
}: ProductCardProps) {
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const { company, can } = useAuth();

  const getCurrencySymbol = () => {
    if ((company as any)?.currency) return (company as any).currency;

    try {
      const storedCompany = localStorage.getItem("company");
      if (storedCompany) {
        const parsed = JSON.parse(storedCompany);
        if (parsed.currency) return parsed.currency;
      }
    } catch (e) {
      console.error("Error reading currency from storage", e);
    }
    return "$";
  };

  const currencySymbol = getCurrencySymbol();

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsDeleteOpen(true);
  };

  const handleConfirmDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete(product.id);
    setIsDeleteOpen(false);
  };

  return (
    <>
      <Card
        className="hover:shadow-md transition-shadow cursor-pointer"
        onClick={() => onClick(product)}
      >
        <CardContent className="p-3">
          <div className="space-y-2.5">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-sm line-clamp-1">
                  {product.name}
                </h3>
                <p className="text-xs text-slate-500 truncate">{product.sku}</p>
              </div>
            </div>

            <p className="text-xs text-slate-600 line-clamp-2 leading-relaxed">
              {product.description}
            </p>

            <div className="flex items-center justify-between pt-1.5 border-t">
              <Badge
                style={{
                  backgroundColor: getCategoryColor(product.categoryId) + "20",
                  color: getCategoryColor(product.categoryId),
                }}
                className="text-xs"
              >
                {getCategoryName(product.categoryId)}
              </Badge>
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-slate-500">{product.unit}</span>
                <div className="flex items-center text-green-600 font-semibold text-sm">
                  {currencySymbol}
                  {product.unitPrice.toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end pt-1.5 border-t">
              <div className="flex gap-1.5">
                {can("products.update") && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 px-2"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(product);
                    }}
                  >
                    <Edit className="h-3 w-3" />
                  </Button>
                )}
                {can("products.delete") && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 px-2 text-red-600 hover:bg-red-50"
                    onClick={handleDeleteClick}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <AlertDialogContent onClick={(e) => e.stopPropagation()}>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Esto eliminará permanentemente
              el producto "{product.name}".
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={(e) => e.stopPropagation()}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export const ProductCard = memo(ProductCardContent);
